<template>
  <div v-if="pending || error || employerProfile || companyData && (companyData.data_pdf || companyData.data_xml)"
       class="dossier-information">
    <transition name="fade" mode="out-in">
      <div key="1" v-if="pending" class="dossier-information__waiting">
        <div class="dossier-information__waiting-img" :style="`background-image: url(${waitPicture})`">
          <div class="dossier-information__waiting-loupe">
            <div :style="`background-image: url(${waitPictureLoupe})`" class="dossier-information__waiting-loupe-inner"></div>
          </div>
        </div>
        <div class="dossier-information__waiting-text">{{
            $tDossier('Wait a minute')
          }}<br>{{ $tDossier('We are looking for information about the company for you') }}
        </div>
      </div>
      <div key="2" v-else-if="error"
           class="dossier-information__waiting-wrap">
        <h3 class="dossier-information__waiting-error">{{error}}</h3>
        <h4 class="dossier-information__waiting-error-message">{{errorMessage}}</h4>
      </div>
      <div key="3" v-else>
        <template v-if="employerProfile">
          <h2 class="dossier-information__title">{{ employerProfile.name }}</h2>
          <div class="dossier-information__head">
            <div class="dossier-information__head-item dossier-information__head-logo">
              <v-lazy-image v-if="employerProfile.company_logo"
                            :src="employerProfile.company_logo"
                            :title="createTitleMixin(employerProfile.name)"
                            :alt="createAltMixin(employerProfile.name)"
                            use-picture
              >
                <source :srcset="employerProfile.company_logo_webp" type="image/webp">
              </v-lazy-image>
              <BaseSvg v-else name="ico-avatar"/>
            </div>
            <div class="dossier-information__head-item dossier-information__head-text">
              <div class="text" v-html="employerProfile.company_information"></div>
            </div>
            <div class="dossier-information__head-item dossier-information__head-contacts">
              <h4 class="dossier-information__head-contacts-title">{{ $tDossier('Representative') }}</h4>
              <div v-if="employerProfile.customer"
                   class="dossier-information__head-contacts-wrap">
                <v-lazy-image v-if="employerProfile.customer.avatar"
                              :src="employerProfile.customer.avatar"
                              :title="createTitleMixin(employerProfile.customer.fullname)"
                              :alt="createAltMixin(employerProfile.customer.fullname)"
                              use-picture
                              class="dossier-information__head-avatar"
                >
                  <source :srcset="employerProfile.customer.avatar_webp" type="image/webp">
                </v-lazy-image>
                <div v-else class="dossier-information__head-avatar">
                  <BaseSvg name="ico-avatar"/>
                </div>
                <div class="dossier-information__contacts-info">
<!--                  <span class="dossier-information__contacts-item _title">{{ $tDossier('The contact person') }}</span>-->
                  <span v-if="employerProfile.customer.fullname"
                        class="dossier-information__contacts-item _name">{{ employerProfile.customer.fullname }}</span>
                  <span v-else class="dossier-information__contacts-item _name">{{ $tGlobal('No name') }}</span>
<!--                  <a class="dossier-information__contacts-item _email"-->
<!--                     v-if="employerProfile.email"-->
<!--                     :href="'mailto:' + employerProfile.email">{{ employerProfile.email }}</a>-->
<!--                  <a class="dossier-information__contacts-item _phone"-->
<!--                     v-if="employerProfile.phone"-->
<!--                     :href="'tel:'+ employerProfile.phone">{{ employerProfile.phone }}</a>-->
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-if="companyData && (companyData.data_pdf || companyData.data_xml)"
             class="dossier-information__general-info dossier-general-info">
          <div v-if="companyData.data_pdf" class="dossier-general-info__col dossier-info-block"
               :class="{'_open': isShowMore}">
            <div class="dossier-info-block__head"
                 @click="isShowMore = !isShowMore"
            >
              <span>{{ $tDossier('General information') }}</span>
              <div class="dossier-info-block__head-btn">
                <BaseSvg name="ico-arrow-down-2"/>
              </div>
            </div>
            <div class="dossier-info-block__body">
              <div class="dossier-info-block__body-group">
                <div class="dossier-info-block__row" v-if="companyData.data_pdf.full_name">
                  <span class="dossier-info-block__row-property">{{ $tDossier('Full name') }}</span>
                  <span class="dossier-info-block__row-value">{{ companyData.data_pdf.full_name }}</span>
                </div>
                <div class="dossier-info-block__row" v-if="companyData.data_pdf.krs">
                  <span class="dossier-info-block__row-property">{{ $tDossier('Code') }} KRS</span>
                  <span class="dossier-info-block__row-value">{{ companyData.data_pdf.krs }}</span>
                </div>
                <div class="dossier-info-block__row" v-if="companyData.data_pdf.nip">
                  <span class="dossier-info-block__row-property">{{ $tDossier('Code') }} NIP</span>
                  <span class="dossier-info-block__row-value">{{ companyData.data_pdf.nip }}</span>
                </div>
                <div class="dossier-info-block__row" v-if="companyData.data_pdf.registration_date">
                  <span class="dossier-info-block__row-property">{{ $tDossier('Registration date') }}</span>
                  <span class="dossier-info-block__row-value">{{ companyData.data_pdf.registration_date }}</span>
                </div>
                <div class="dossier-info-block__row" v-if="companyData.data_pdf.site">
                  <span class="dossier-info-block__row-property">{{ $tDossier('Site') }}</span>
                  <span class="dossier-info-block__row-value">{{ companyData.data_pdf.site }}</span>
                </div>
                <div class="dossier-info-block__row" v-if="companyData.data_pdf.main_activity">
                  <span class="dossier-info-block__row-property">{{ $tDossier('Type of activity (main)') }}</span>
                  <span class="dossier-info-block__row-value">{{ companyData.data_pdf.main_activity }}</span>
                </div>
              </div>
              <collapse-transition>
                <div v-if="isShowMore" class="dossier-info-block__body-group">
                  <div class="dossier-info-block__row" v-if="companyData.data_pdf.address">
                    <span class="dossier-info-block__row-property">{{ $tDossier('Address') }}</span>
                    <span class="dossier-info-block__row-value">{{ companyData.data_pdf.address }}</span>
                  </div>
                  <div class="dossier-info-block__row" v-if="companyData.data_pdf.capital">
                    <span class="dossier-info-block__row-property">{{ $tDossier('Capital') }}</span>
                    <span class="dossier-info-block__row-value">{{ companyData.data_pdf.capital }}</span>
                  </div>
                  <div class="dossier-info-block__row"
                       v-if="companyData.data_pdf.owners && companyData.data_pdf.owners.length">
                    <span class="dossier-info-block__row-property">{{ $tDossier('Owners') }}</span>
                    <div class="dossier-info-block__row-value">
                      <ul>
                        <li v-for="item in companyData.data_pdf.owners"
                            :key="item">{{ item }}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="dossier-info-block__row"
                       v-if="companyData.data_pdf.management && companyData.data_pdf.management.length">
                    <span class="dossier-info-block__row-property">{{ $tDossier('Leadership') }}</span>
                    <div class="dossier-info-block__row-value">
                      <ul>
                        <li v-for="item in companyData.data_pdf.management"
                            :key="item">{{ item }}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="dossier-info-block__row"
                       v-if="companyData.data_pdf.all_activity && companyData.data_pdf.all_activity.length">
                    <span class="dossier-info-block__row-property">{{ $tDossier('Type of activity (list)') }}</span>
                    <div class="dossier-info-block__row-value">
                      <ul>
                        <li v-for="item in companyData.data_pdf.all_activity"
                            :key="item">{{ item }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </collapse-transition>

            </div>
          </div>

          <div v-if="companyData.data_xml" class="dossier-general-info__col dossier-info-block">
            <div class="dossier-info-block__item-group">
              <div class="dossier-info-block__item" :class="{
              _red: !companyData.data_xml.courts_encumbrances,
            _green: !!companyData.data_xml.courts_encumbrances}">
                <h4 class="dossier-info-block__item-title">{{ $tDossier('Courts, encumbrance') }}</h4>
                <div class="dossier-info-block__item-content">
                  <div class="dossier-info-block__item-content-icon">
                    <BaseSvg name="ico-check-outline-2" class="_check"/>
                    <BaseSvg name="ico-accent" class="_accent"/>
                    <BaseSvg name="ico-close-outline" class="_close"/>
                  </div>
                  <div class="dossier-info-block__item-content-descr text">
                    <p>{{ companyData.data_xml.courts_encumbrances_text }}</p>
                  </div>
                </div>
              </div>
              <div class="dossier-info-block__item" :class="`_${companyData.data_xml.color}`">
                <h4 class="dossier-info-block__item-title">{{ $tDossier('Financial indicators') }}</h4>
                <div class="dossier-info-block__item-content">
                  <div class="dossier-info-block__item-content-icon"
                  >{{ companyData.data_xml.mark }}
                  </div>
                  <div class="dossier-info-block__item-content-descr text">
                    <p>{{ companyData.data_xml.mark_text }}</p>
                  </div>
                </div>
              </div>
            </div>

            <collapse-transition>
              <div v-if="isShowMore" class="dossier-info-block__table">
                <div class="dossier-info-block__table-container">
                  <div class="dossier-info-block__table-item"
                       v-for="(item, index) in companyData.data_xml.data_with_labels"
                       :key="index">
                    <h6 class="dossier-info-block__table-title">{{ $tDossier(index) }}</h6>
                    <div class="dossier-info-block__table-body">
                      <div class="dossier-info-block__table-body-row">
                        <span class="dossier-info-block__table-property">{{ item.last_year.label }}</span>
                        <span class="dossier-info-block__table-value">{{ item.last_year.value }}</span>
                      </div>
                      <div class="dossier-info-block__table-body-row">
                        <span class="dossier-info-block__table-property">{{ item.pen_year.label }}</span>
                        <span class="dossier-info-block__table-value">{{ item.pen_year.value }}</span>
                      </div>
                      <div class="dossier-info-block__table-body-row">
                        <span class="dossier-info-block__table-property">{{ item.percent.label }}</span>
                        <span class="dossier-info-block__table-value">{{ item.percent.value }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </collapse-transition>
          </div>
          <!-----demo----->
          <!--<div v-else class="dossier-general-info__col dossier-info-block">
            <div class="dossier-info-block__item-group">
              <div class="dossier-info-block__item _green">
                <h4 class="dossier-info-block__item-title">{{ $tDossier('Courts, encumbrance') }}</h4>
                <div class="dossier-info-block__item-content">
                  <div class="dossier-info-block__item-content-icon">
                    <BaseSvg name="ico-check-outline-2" class="_check"/>
                    <BaseSvg name="ico-accent" class="_accent"/>
                    <BaseSvg name="ico-close-outline" class="_close"/>
                  </div>
                  <div class="dossier-info-block__item-content-descr text">
                    <p>Lorem ipsum dolor sit amet, consectetur adipisicing elit. Accusamus, temporibus.</p>
                  </div>
                </div>
              </div>
              <div class="dossier-info-block__item _green">
                <h4 class="dossier-info-block__item-title">{{ $tDossier('Financial indicators') }}</h4>
                <div class="dossier-info-block__item-content">
                  <div class="dossier-info-block__item-content-icon"
                  >5
                  </div>
                  <div class="dossier-info-block__item-content-descr text">
                    <p>Lorem ipsum dolor sit amet, consectetur adipisicing elit. Atque, blanditiis?</p>
                  </div>
                </div>
              </div>
            </div>

            <collapse-transition>
              <div v-if="isShowMore" class="dossier-info-block__table">
                <div class="dossier-info-block__table-container">
                  <div class="dossier-info-block__table-item"
                       v-for="(item, index) in 8"
                       :key="index">
                    <h6 class="dossier-info-block__table-title">sasdas</h6>
                    <div class="dossier-info-block__table-body">
                      <div class="dossier-info-block__table-body-row">
                        <span class="dossier-info-block__table-property">123</span>
                        <span class="dossier-info-block__table-value">123123123</span>
                      </div>
                      <div class="dossier-info-block__table-body-row">
                        <span class="dossier-info-block__table-property">dasasd</span>
                        <span class="dossier-info-block__table-value">21231</span>
                      </div>
                      <div class="dossier-info-block__table-body-row">
                        <span class="dossier-info-block__table-property">12312</span>
                        <span class="dossier-info-block__table-value">asdasd</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </collapse-transition>
          </div>-->
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import waitPicture from '@/assets/img/svg/wait-search.svg'
  import waitPictureLoupe from '@/assets/img/svg/wait-search-loupe.svg'
  import {CollapseTransition} from 'vue2-transitions'

  export default {
    name: 'InformationDossier',
    components: {
      CollapseTransition
    },
    props: {
      pending: {
        type: Boolean,
        default: false
      },
      companyData: {
        type: Object
      },
      employerProfile: {
        type: Object
      },
      error: String,
      errorMessage: String,
    },
    data() {
      return {
        waitPicture,
        waitPictureLoupe,
        isShowMore: false
      }
    },
  }
</script>

<style>

</style>
